import { QueryClientProvider, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  useNavigate,
  useSearchParams,
  Navigate,
} from "react-router-dom";
import { queryClient } from "@/lib/react-query";
import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/app-sidebar";
import { SecurityDashboard } from "@/pages/SecurityDashboard";
import { IssuesPage } from "@/pages/IssuesPage";
import { IssueDetailPage } from "@/pages/IssueDetailPage";
import { AgentDetailPage } from "@/pages/AgentDetailPage";
import { AgentsListPage } from "@/pages/AgentsListPage";
import { AlertsListPage } from "@/pages/AlertsListPage";
import { CreateAlertPage } from "@/pages/CreateAlertPage";
import { EditAlertPage } from "@/pages/EditAlertPage";
import { LoginPage } from "@/pages/LoginPage";
import { DemoFlowPage } from "@/pages/DemoFlowPage";
import { LLMScansListPage } from "@/pages/LLMScansListPage";
import { LLMScanResultsPage } from "@/pages/LLMScanResultsPage";
import { AiInventoryPage } from "@/pages/AiInventoryPage";
import { SharedReportPage } from "@/pages/SharedReportPage";
import { SettingsPage } from "@/pages/SettingsPage";
import { IntegrationSetupPage } from "@/pages/IntegrationSetupPage";
import { Button } from "@/components/ui/button";
import { AuthProvider } from "@/auth";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Printer, Mail } from "lucide-react";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { FeatureGate } from "@/components/auth/FeatureGate";
import { OrganizationProvider } from "@/providers/OrganizationProvider";
import { NoOrganizationGuard } from "@/components/organization/NoOrganizationGuard";
import { PostHogPageviewTracker } from "@/components/analytics/PostHogPageviewTracker";
import { CrispChat } from "@/components/analytics/CrispChat";
import { AppTourStylesProvider } from "@/providers/AppTourStylesProvider";
import { AppTourLauncher } from "@/components/onboarding/AppTourLauncher";
import { useIntegrationSetupRedirect } from "@/hooks/useIntegrationSetupRedirect";
import { apiClient } from "@/services/api";
import { useScanQuota } from "@/hooks/useScanQuota";
import { Toaster } from "@/components/ui/sonner";
import { UiSandboxBadge } from "@/components/dev/UiSandboxBadge";
import type { UserData } from "@/types/user";
import { useOrganizationContext } from "@/providers/OrganizationProvider";
import { TenantProvider, useTenant } from "@/providers/TenantProvider";
import "./App.css";

/**
 * Render the application's dashboard welcome panel.
 *
 * @returns The dashboard UI as a React element.
 */
function Dashboard() {
  return (
    <div className="flex flex-1 flex-col gap-4 p-6">
      <div className="rounded-lg border bg-card p-6">
        <h2 className="text-2xl font-semibold mb-4">Welcome to Vortal</h2>
        <p className="text-muted-foreground">
          Your platform is ready. Use the sidebar to navigate between Dashboard, Security Issues,
          Alerts & Policies, and Settings.
        </p>
      </div>
    </div>
  );
}

/**
 * Component that checks if user needs integration setup and redirects accordingly.
 */
function IntegrationSetupRedirect({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const { shouldRedirectToSetup, isLoading } = useIntegrationSetupRedirect();

  // Don't redirect if we're already on the setup page
  if (location.pathname === "/setup/integrations") {
    return <>{children}</>;
  }

  // Show nothing while loading to prevent flash
  if (isLoading) {
    return null;
  }

  // Redirect to setup page if needed, preserving the intended destination
  if (shouldRedirectToSetup) {
    const returnTo = location.pathname + location.search;
    return <Navigate to={`/setup/integrations?returnTo=${encodeURIComponent(returnTo)}`} replace />;
  }

  return <>{children}</>;
}

/**
 * Wrapper component for protected app content.
 * Uses redirect-based integration setup instead of modals.
 */
function ProtectedAppContent() {
  return (
    <TenantProvider>
    <IntegrationSetupRedirect>
      <Routes>
        {/* Integration setup page - full-page experience */}
        <Route path="/setup/integrations" element={<IntegrationSetupPage />} />

        {/* Main app layout */}
        <Route
          path="/*"
          element={
            <SidebarProvider>
              <AppSidebar />
              <main className="flex-1 min-w-0">
                <AppHeader />
                <AppTourLauncher />
                <Routes>
                  <Route path="/" element={<Navigate to="/compliance" replace />} />
                  <Route
                    path="/security"
                    element={
                      <FeatureGate feature="security_dashboard">
                        <SecurityDashboard />
                      </FeatureGate>
                    }
                  />
                  <Route
                    path="/agents"
                    element={
                      <FeatureGate feature="security_dashboard">
                        <AgentsListPage />
                      </FeatureGate>
                    }
                  />
                  <Route
                    path="/issues"
                    element={
                      <FeatureGate feature="issues">
                        <IssuesPage />
                      </FeatureGate>
                    }
                  />
                  <Route
                    path="/issues/:issueId"
                    element={
                      <FeatureGate feature="issues">
                        <IssueDetailPage />
                      </FeatureGate>
                    }
                  />
                  <Route
                    path="/agents/:agentId"
                    element={
                      <FeatureGate feature="security_dashboard">
                        <AgentDetailPage />
                      </FeatureGate>
                    }
                  />
                  <Route
                    path="/alerts"
                    element={
                      <FeatureGate feature="alerts">
                        <AlertsListPage />
                      </FeatureGate>
                    }
                  />
                  <Route
                    path="/alerts/new"
                    element={
                      <FeatureGate feature="alerts">
                        <CreateAlertPage />
                      </FeatureGate>
                    }
                  />
                  <Route
                    path="/alerts/:id/edit"
                    element={
                      <FeatureGate feature="alerts">
                        <EditAlertPage />
                      </FeatureGate>
                    }
                  />
                  <Route
                    path="/compliance"
                    element={
                      <FeatureGate feature="demo_flow">
                        <DemoFlowPage />
                      </FeatureGate>
                    }
                  />
                  <Route
                    path="/llm-scans"
                    element={
                      <FeatureGate feature="llm_scans">
                        <LLMScansListPage />
                      </FeatureGate>
                    }
                  />
                  <Route
                    path="/llm-scans/:scanUuid/results"
                    element={
                      <FeatureGate feature="llm_scans">
                        <LLMScanResultsPage />
                      </FeatureGate>
                    }
                  />
                  <Route
                    path="/ai-inventory"
                    element={
                      <FeatureGate feature="security_dashboard">
                        <AiInventoryPage />
                      </FeatureGate>
                    }
                  />
                  <Route path="/settings" element={<SettingsPage />} />
                  <Route
                    path="/billing"
                    element={<Navigate to="/settings?section=billing" replace />}
                  />
                  <Route
                    path="/profile"
                    element={<Navigate to="/settings?section=profile" replace />}
                  />
                </Routes>
              </main>
            </SidebarProvider>
          }
        />
      </Routes>
    </IntegrationSetupRedirect>
    </TenantProvider>
  );
}

function AppHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { organization } = useOrganizationContext();
  const { selectedTenantSlug, accessibleOrgs, setTenant, isMssp } = useTenant();
  const showTenantSelector = isMssp && ["/security", "/agents", "/issues"].includes(location.pathname);
  const isAgentDetailPage = location.pathname.startsWith("/agents/");
  type OverviewRange = "24h" | "7d" | "30d" | "90d";
  const selectedRange = (searchParams.get("range") as OverviewRange | null) ?? "30d";

  const updateOverviewParam = (key: "tenant" | "range", value: string) => {
    if (key === "tenant") {
      setTenant(value);
    } else {
      const next = new URLSearchParams(searchParams);
      next.set(key, value);
      setSearchParams(next, { replace: true });
    }
  };

  // Fetch current user to get username
  const { data: userData } = useQuery<UserData>({
    queryKey: ["user", "me"],
    queryFn: async () => {
      const data = await apiClient.get<UserData>("/api/v1/user/me/");
      return data;
    },
    staleTime: 30000,
  });

  // Fetch scan quota
  const { data: quota, isLoading: quotaLoading } = useScanQuota(userData?.username);

  return (
    <header className="flex h-[68px] shrink-0 items-center justify-between gap-3 bg-background px-4 lg:px-6">
      {isAgentDetailPage ? (
        <Button
          variant="outline"
          onClick={() => navigate("/agents")}
          className="h-9 gap-2 rounded-full bg-white px-4 shadow-none"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Agents
        </Button>
      ) : (
        <div />
      )}
      <div className="ml-auto flex items-center gap-4">
        {showTenantSelector && (
          <div className="flex min-w-0 items-center gap-2">
            <Select
              value={selectedTenantSlug}
              onValueChange={(value) => updateOverviewParam("tenant", value)}
            >
              <SelectTrigger className="h-9 w-[180px] rounded-[50px] bg-white text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-[20px] bg-white p-2">
                <SelectItem value="all">All tenants ({accessibleOrgs?.length ?? 1})</SelectItem>
                {(accessibleOrgs.length > 0 ? accessibleOrgs : organization ? [organization] : []).map((org) => {
                  const slug = org.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
                  return (
                    <SelectItem key={org.id} value={slug}>{org.name}</SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            <div
              className="hidden h-9 w-[150px] items-center rounded-[50px] border bg-white p-0.5 sm:flex"
              aria-label="Global time range"
            >
              {(["24h", "7d", "30d", "90d"] as OverviewRange[]).map((range) => (
                <Button
                  key={range}
                  variant="ghost"
                  size="sm"
                  className={`h-7 flex-1 rounded-[50px] bg-white px-1 text-xs hover:bg-white ${selectedRange === range ? "bg-foreground/5 hover:bg-foreground/5" : ""}`}
                  onClick={() => updateOverviewParam("range", range)}
                >
                  {range}
                </Button>
              ))}
            </div>
          </div>
        )}
        {/* Scan Quota Display */}
        {!quotaLoading && quota && !quota.is_unlimited && (
          <div className="text-sm" data-tour="quota-counter">
            <span className="text-muted-foreground">Reports used: </span>
            <span className="font-medium">
              {quota.used_reports}/{quota.max_reports ?? "unlimited"}
            </span>
            {!quota.is_unlimited &&
              quota.max_reports != null &&
              quota.used_reports >= quota.max_reports && (
                <a
                  href="mailto:support@vortal.so?subject=Increase Report Limit"
                  className="ml-2 text-xs text-primary hover:underline"
                >
                  Upgrade
                </a>
              )}
          </div>
        )}

        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            asChild
            className="flex h-9 w-[150px] items-center justify-center gap-2 rounded-[50px] border bg-white px-3 py-1 hover:bg-white/90"
            data-tour="feedback-button"
          >
            <a
              href="mailto:hello@vortal.so?subject=Vortal Feedback"
              title="Send us feedback – we want your input!"
              style={{ display: "flex", alignItems: "center", textDecoration: "none" }}
            >
              <Mail className="h-5 w-5 mr-1" />
              <span className="hidden sm:inline font-medium">Send Feedback</span>
              <span className="sr-only">Send feedback (opens mail client)</span>
            </a>
          </Button>
          {showTenantSelector && (
            <Button
              variant="ghost"
              size="icon"
              className="rounded-[50px] border bg-white hover:bg-white/90"
              onClick={() => window.print()}
            >
              <Printer className="h-5 w-5" />
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}

/**
 * Root application component that configures global providers, routing, and the main app layout.
 *
 * Renders the QueryClient, router, and authentication providers, exposes public routes (e.g., login and shared reports),
 * and the authenticated application layout containing the sidebar, header, and gated protected routes.
 *
 * @returns The top-level React element for the application, including providers and route definitions.
 */
function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <PostHogPageviewTracker />
        <AuthProvider>
          <AppTourStylesProvider>
            <CrispChat />
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/share/:token" element={<SharedReportPage />} />

              {/* Protected routes - require authentication */}
              <Route
                path="/*"
                element={
                  <ProtectedRoute>
                    <OrganizationProvider>
                      <NoOrganizationGuard>
                        <ProtectedAppContent />
                      </NoOrganizationGuard>
                    </OrganizationProvider>
                  </ProtectedRoute>
                }
              />
            </Routes>
            <ReactQueryDevtools initialIsOpen={false} />
          </AppTourStylesProvider>
        </AuthProvider>
      </BrowserRouter>
      <Toaster />
      <UiSandboxBadge />
    </QueryClientProvider>
  );
}

export default App;
